import { ReactNode, SyntheticEvent, useCallback, useState } from 'react'

import { PickerItem } from '../types'
import { shouldDisplay } from '../utils/shouldDisplay'
import { SearchChange } from './SearchBar'

export type SearchBy<Item extends PickerItem> = (
  keyword: string,
  label: ReactNode,
  item: Item,
) => boolean

interface SearchResult<Item extends PickerItem> {
  keyword: string
  filteredData: Item[]
  event: SyntheticEvent
}

interface UseSearchParams<Item extends PickerItem> {
  data: Item[]
  searchBy?: SearchBy<Item>
  onSearchResult: (result: SearchResult<Item>) => void
}

interface DisplayCheck<Item extends PickerItem> {
  item: Item
  keyword?: string
}

export const useSearch = <Item extends PickerItem>({
  data,
  searchBy,
  onSearchResult,
}: UseSearchParams<Item>) => {
  const [searchKeyword, setSearchKeyword] = useState('')

  const checkShouldDisplay = useCallback(
    ({ item, keyword = searchKeyword }: DisplayCheck<Item>) =>
      searchBy
        ? searchBy(keyword, item.label, item)
        : shouldDisplay({ label: item.label, keyword }),
    [searchBy, searchKeyword],
  )

  const handleSearch = ({ keyword, event }: SearchChange) => {
    const filteredData = data.filter(item => checkShouldDisplay({ item, keyword }))
    setSearchKeyword(keyword)
    onSearchResult({ keyword, filteredData, event })
  }

  return { searchKeyword, setSearchKeyword, checkShouldDisplay, handleSearch }
}
